import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Play, Clock, TrendUp, TrendDown, Newspaper, Trophy, Target, Lightning } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from 'recharts';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';

// Sample companies for Learn Mode
const LEARN_COMPANIES = [
  { symbol: 'TECHCO', name: 'TechCo Industries', sector: 'Technology', basePrice: 150 },
  { symbol: 'PHARMA', name: 'PharmaCorp', sector: 'Healthcare', basePrice: 85 },
  { symbol: 'ENERGY', name: 'GreenEnergy Inc', sector: 'Energy', basePrice: 120 },
  { symbol: 'RETAIL', name: 'ShopMart Corp', sector: 'Retail', basePrice: 65 },
  { symbol: 'FINTECH', name: 'PayFlow Systems', sector: 'Finance', basePrice: 200 },
];

// News templates for different scenarios
const NEWS_TEMPLATES = {
  positive: [
    { text: '📈 {company} reports record quarterly earnings', impact: 0.05 },
    { text: '✅ FDA approves {company}\'s new product', impact: 0.08 },
    { text: '🤝 {company} announces major partnership deal', impact: 0.04 },
    { text: '📊 Analysts upgrade {company} to "Strong Buy"', impact: 0.03 },
    { text: '🚀 {company} launches innovative new product line', impact: 0.06 },
    { text: '💰 {company} raises full-year guidance', impact: 0.04 },
  ],
  negative: [
    { text: '📉 {company} misses earnings expectations', impact: -0.05 },
    { text: '❌ Regulators delay {company} product approval', impact: -0.07 },
    { text: '⚠️ {company} faces unexpected supply chain issues', impact: -0.04 },
    { text: '📊 Analysts downgrade {company} to "Hold"', impact: -0.03 },
    { text: '🔴 {company} announces executive departure', impact: -0.03 },
    { text: '⬇️ {company} lowers revenue forecast', impact: -0.05 },
  ],
  neutral: [
    { text: '📰 {company} to present at industry conference', impact: 0.01 },
    { text: '🔄 {company} announces routine board changes', impact: 0 },
    { text: '📋 {company} files standard regulatory paperwork', impact: 0 },
  ]
};

const LearnMode = () => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState('menu'); // menu, playing, results
  const [level, setLevel] = useState(1);
  const [company, setCompany] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(0);
  const [entryPrice, setEntryPrice] = useState(null);
  const [position, setPosition] = useState(null); // null, 'long'
  const [shares, setShares] = useState(0);
  const [cash, setCash] = useState(10000);
  const [news, setNews] = useState([]);
  const [timeLeft, setTimeLeft] = useState(60);
  const [decisions, setDecisions] = useState([]);
  const [score, setScore] = useState(0);
  const gameLoopRef = useRef(null);
  const newsIntervalRef = useRef(null);

  const levelConfig = {
    1: { duration: 60, newsFrequency: 8000, volatility: 0.02, name: 'Beginner' },
    2: { duration: 90, newsFrequency: 6000, volatility: 0.025, name: 'Novice' },
    3: { duration: 120, newsFrequency: 5000, volatility: 0.03, name: 'Intermediate' },
    4: { duration: 150, newsFrequency: 4000, volatility: 0.035, name: 'Advanced' },
    5: { duration: 180, newsFrequency: 3000, volatility: 0.04, name: 'Expert' },
  };

  const startGame = useCallback((selectedLevel) => {
    const config = levelConfig[selectedLevel];
    const selectedCompany = LEARN_COMPANIES[Math.floor(Math.random() * LEARN_COMPANIES.length)];
    
    setLevel(selectedLevel);
    setCompany(selectedCompany);
    setCurrentPrice(selectedCompany.basePrice);
    setPriceHistory([{ time: 0, price: selectedCompany.basePrice }]);
    setTimeLeft(config.duration);
    setCash(10000);
    setShares(0);
    setPosition(null);
    setEntryPrice(null);
    setNews([]);
    setDecisions([]);
    setScore(0);
    setGameState('playing');
  }, []);

  // Game loop - update price
  useEffect(() => {
    if (gameState !== 'playing') return;

    const config = levelConfig[level];
    let time = 0;

    gameLoopRef.current = setInterval(() => {
      time += 1;
      
      setCurrentPrice(prev => {
        const randomChange = (Math.random() - 0.5) * config.volatility * prev;
        const newPrice = Math.max(1, prev + randomChange);
        
        setPriceHistory(history => {
          const newHistory = [...history, { time, price: newPrice }];
          return newHistory.slice(-60); // Keep last 60 points
        });
        
        return newPrice;
      });

      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(gameLoopRef.current);
          clearInterval(newsIntervalRef.current);
          setGameState('results');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(gameLoopRef.current);
  }, [gameState, level]);

  // News generation
  useEffect(() => {
    if (gameState !== 'playing' || !company) return;

    const config = levelConfig[level];

    const generateNews = () => {
      const rand = Math.random();
      let category;
      if (rand < 0.4) category = 'positive';
      else if (rand < 0.8) category = 'negative';
      else category = 'neutral';

      const templates = NEWS_TEMPLATES[category];
      const template = templates[Math.floor(Math.random() * templates.length)];
      
      const newsItem = {
        id: Date.now(),
        text: template.text.replace('{company}', company.name),
        impact: template.impact,
        category,
        time: new Date().toLocaleTimeString()
      };

      setNews(prev => [newsItem, ...prev].slice(0, 5));

      // Apply impact to price
      setCurrentPrice(prev => {
        const impactedPrice = prev * (1 + template.impact);
        return Math.max(1, impactedPrice);
      });
    };

    // Generate first news after 3 seconds
    setTimeout(generateNews, 3000);
    
    newsIntervalRef.current = setInterval(generateNews, config.newsFrequency);

    return () => clearInterval(newsIntervalRef.current);
  }, [gameState, company, level]);

  const handleBuy = () => {
    if (cash <= 0 || position === 'long') return;
    
    const sharesToBuy = Math.floor(cash / currentPrice);
    if (sharesToBuy <= 0) return;

    const cost = sharesToBuy * currentPrice;
    setCash(prev => prev - cost);
    setShares(sharesToBuy);
    setPosition('long');
    setEntryPrice(currentPrice);

    setDecisions(prev => [...prev, {
      type: 'BUY',
      price: currentPrice,
      shares: sharesToBuy,
      time: timeLeft,
      latestNews: news[0]?.text || 'No news'
    }]);
  };

  const handleSell = () => {
    if (shares <= 0) return;

    const proceeds = shares * currentPrice;
    const profit = proceeds - (shares * entryPrice);
    
    setCash(prev => prev + proceeds);
    setScore(prev => prev + profit);
    
    setDecisions(prev => [...prev, {
      type: 'SELL',
      price: currentPrice,
      shares: shares,
      profit: profit,
      time: timeLeft,
      latestNews: news[0]?.text || 'No news'
    }]);

    setShares(0);
    setPosition(null);
    setEntryPrice(null);
  };

  const calculateFinalScore = () => {
    const portfolioValue = cash + (shares * currentPrice);
    const totalReturn = ((portfolioValue - 10000) / 10000) * 100;
    const correctDecisions = decisions.filter(d => {
      if (d.type === 'BUY' && d.latestNews?.includes('📈')) return true;
      if (d.type === 'SELL' && d.latestNews?.includes('📉')) return true;
      return d.profit > 0;
    }).length;
    
    return {
      portfolioValue,
      totalReturn,
      totalDecisions: decisions.length,
      correctDecisions,
      accuracy: decisions.length > 0 ? (correctDecisions / decisions.length * 100) : 0
    };
  };

  // Menu Screen
  if (gameState === 'menu') {
    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <button
            onClick={() => navigate('/game-modes')}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Game Modes
          </button>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex p-4 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 mb-6">
              <Target size={48} weight="fill" className="text-white" />
            </div>
            <h1 className="text-4xl font-unbounded font-bold text-white mb-4">Learn Mode</h1>
            <p className="text-zinc-400 max-w-xl mx-auto">
              React to news headlines and make quick trading decisions. Each level increases in difficulty with faster news and longer duration.
            </p>
          </motion.div>

          <div className="grid gap-4">
            {Object.entries(levelConfig).map(([lvl, config]) => (
              <motion.button
                key={lvl}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Number(lvl) * 0.1 }}
                onClick={() => startGame(Number(lvl))}
                className="flex items-center justify-between p-6 rounded-xl bg-zinc-900/50 border border-zinc-800 hover:border-emerald-500/50 transition-all group"
                data-testid={`level-${lvl}-btn`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-xl">
                    {lvl}
                  </div>
                  <div className="text-left">
                    <h3 className="text-white font-bold text-lg">{config.name}</h3>
                    <p className="text-zinc-500 text-sm">
                      {config.duration}s duration • News every {config.newsFrequency/1000}s
                    </p>
                  </div>
                </div>
                <Play size={24} className="text-zinc-600 group-hover:text-emerald-400 transition-colors" />
              </motion.button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Playing Screen
  if (gameState === 'playing') {
    const priceChange = entryPrice ? ((currentPrice - entryPrice) / entryPrice * 100) : 0;
    const portfolioValue = cash + (shares * currentPrice);

    return (
      <div className="min-h-screen noise-bg">
        <div className="max-w-7xl mx-auto px-6 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-unbounded font-bold text-white">{company?.name}</h2>
              <p className="text-zinc-500">{company?.symbol} • {company?.sector}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-zinc-500 text-sm">Level {level}</p>
                <p className="text-emerald-400 font-bold">{levelConfig[level].name}</p>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-xl ${timeLeft <= 10 ? 'bg-red-500/20 text-red-400' : 'bg-zinc-800 text-white'}`}>
                <Clock size={20} />
                <span className="font-mono text-xl font-bold">{timeLeft}s</span>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Chart */}
            <div className="lg:col-span-2 bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-4xl font-bold text-white">${currentPrice.toFixed(2)}</p>
                  {entryPrice && (
                    <p className={`text-sm ${priceChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}% from entry
                    </p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-zinc-500 text-sm">Portfolio Value</p>
                  <p className="text-2xl font-bold text-white">${portfolioValue.toFixed(2)}</p>
                </div>
              </div>

              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceHistory}>
                    <XAxis dataKey="time" hide />
                    <YAxis domain={['auto', 'auto']} hide />
                    {entryPrice && <ReferenceLine y={entryPrice} stroke="#f59e0b" strokeDasharray="5 5" />}
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke={currentPrice >= (priceHistory[0]?.price || 0) ? '#10b981' : '#ef4444'}
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <Button
                  onClick={handleBuy}
                  disabled={cash <= 0 || position === 'long'}
                  className="flex-1 py-6 text-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  data-testid="buy-btn"
                >
                  <TrendUp size={24} className="mr-2" />
                  BUY
                </Button>
                <Button
                  onClick={handleSell}
                  disabled={shares <= 0}
                  className="flex-1 py-6 text-lg bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  data-testid="sell-btn"
                >
                  <TrendDown size={24} className="mr-2" />
                  SELL
                </Button>
              </div>

              {/* Position Info */}
              <div className="grid grid-cols-3 gap-4 mt-4">
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <p className="text-zinc-500 text-sm">Cash</p>
                  <p className="text-white font-bold">${cash.toFixed(2)}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <p className="text-zinc-500 text-sm">Shares</p>
                  <p className="text-white font-bold">{shares}</p>
                </div>
                <div className="bg-zinc-800/50 rounded-xl p-4 text-center">
                  <p className="text-zinc-500 text-sm">Position</p>
                  <p className={`font-bold ${position ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {position ? 'LONG' : 'NONE'}
                  </p>
                </div>
              </div>
            </div>

            {/* News Feed */}
            <div className="bg-zinc-900/50 rounded-2xl p-6 border border-zinc-800">
              <div className="flex items-center gap-2 mb-4">
                <Newspaper size={24} className="text-yellow-400" />
                <h3 className="text-white font-bold text-lg">Live News Feed</h3>
              </div>

              <AnimatePresence>
                {news.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">Waiting for news...</p>
                ) : (
                  <div className="space-y-3">
                    {news.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        className={`p-4 rounded-xl border ${
                          item.category === 'positive' 
                            ? 'bg-emerald-500/10 border-emerald-500/30' 
                            : item.category === 'negative'
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-zinc-800/50 border-zinc-700'
                        }`}
                      >
                        <p className="text-white text-sm">{item.text}</p>
                        <p className="text-zinc-500 text-xs mt-1">{item.time}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Results Screen
  if (gameState === 'results') {
    const results = calculateFinalScore();
    const passed = results.totalReturn > 0;

    return (
      <div className="min-h-screen noise-bg">
        <Navbar />
        <div className="max-w-2xl mx-auto px-6 py-12">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className={`inline-flex p-6 rounded-full ${passed ? 'bg-emerald-500/20' : 'bg-red-500/20'} mb-6`}>
              <Trophy size={64} weight="fill" className={passed ? 'text-emerald-400' : 'text-red-400'} />
            </div>

            <h1 className="text-3xl font-unbounded font-bold text-white mb-2">
              Level {level} {passed ? 'Complete!' : 'Finished'}
            </h1>
            <p className="text-zinc-400 mb-8">
              {passed ? 'Great job! You made a profit.' : 'Keep practicing to improve your skills.'}
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Final Portfolio</p>
                <p className="text-2xl font-bold text-white">${results.portfolioValue.toFixed(2)}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Total Return</p>
                <p className={`text-2xl font-bold ${results.totalReturn >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {results.totalReturn >= 0 ? '+' : ''}{results.totalReturn.toFixed(2)}%
                </p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Decisions Made</p>
                <p className="text-2xl font-bold text-white">{results.totalDecisions}</p>
              </div>
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800">
                <p className="text-zinc-500 text-sm">Accuracy</p>
                <p className="text-2xl font-bold text-yellow-400">{results.accuracy.toFixed(0)}%</p>
              </div>
            </div>

            {/* Decision Replay */}
            {decisions.length > 0 && (
              <div className="bg-zinc-900/50 rounded-xl p-6 border border-zinc-800 mb-8 text-left">
                <h3 className="text-white font-bold mb-4">Your Decisions</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {decisions.map((d, i) => (
                    <div key={i} className={`p-3 rounded-lg ${d.type === 'BUY' ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                      <div className="flex justify-between">
                        <span className={`font-bold ${d.type === 'BUY' ? 'text-emerald-400' : 'text-red-400'}`}>
                          {d.type} {d.shares} @ ${d.price.toFixed(2)}
                        </span>
                        {d.profit !== undefined && (
                          <span className={d.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {d.profit >= 0 ? '+' : ''}${d.profit.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <p className="text-zinc-500 text-xs mt-1">{d.latestNews}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-4">
              <Button
                onClick={() => startGame(level)}
                className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700"
              >
                Retry Level {level}
              </Button>
              {passed && level < 5 && (
                <Button
                  onClick={() => startGame(level + 1)}
                  className="flex-1 py-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  Next Level →
                </Button>
              )}
            </div>

            <button
              onClick={() => setGameState('menu')}
              className="mt-4 text-zinc-500 hover:text-white transition-colors"
            >
              Back to Level Select
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return null;
};

export default LearnMode;
